import {
  exists,
  filterMaybeArray,
  itemKey,
  parseAwsJson,
  shuffleArray,
} from "@examspark/common";
import {
  ClarificationData,
  Classroom,
  ClassroomData,
  ConceptualAlignmentData,
  ElementNew,
  ExamConfigData,
  Maybe,
  Objective,
  ObjectivesItem,
  ObjectivesView,
  ObjectivesViewType,
  ParsedClassroom,
  ParsedExam,
  ParsedExamConfig,
  ParsedExamSession,
  ParsedItem,
  ParsedObjectivesItem,
  ParsedObjectivesView,
  ParsedSearchableResourceConnectionNew,
  ParsedTCMPCUnit,
  ParsedUnitObjective,
  ParsedWorkspaceShare,
  ParsedWorkspaceUser,
  Replace,
  Resource,
  ResourceContent,
  ResourceElement,
  SameKeys,
  SearchableResourceConnectionNew,
  SearchableResourceFilter,
  Stem,
  VerticalAlignmentData,
  Workspace,
  WorkspaceAcl,
  WorkspaceShare,
  WorkspaceShareDefinition,
  ParsedExamAttempt,
  ExamAttempt,
  AnyObject,
  ExamSessionResults,
  ExamSession,
  InteractionTypeEnum,
  ExamBlobFiles,
  ExamHighlights,
  Product,
  ProductDefinition,
  ParsedProduct,
} from "@examspark/types";

import {
  getAlignmentsSafely,
  getContextsSafely,
  parseItemCanvas,
} from "../utils";

export function parseClassroom(classroom: Classroom): ParsedClassroom;
export function parseClassroom(
  classroom: Classroom | undefined,
): ParsedClassroom | undefined;
export function parseClassroom(
  classroom: Classroom | null | undefined,
): ParsedClassroom | null | undefined;
export function parseClassroom(
  classroom: Classroom | null | undefined,
): ParsedClassroom | null | undefined {
  return parseResource(classroom, (r) => ({
    unit: r.unit as ParsedTCMPCUnit | undefined,
    data: parseAwsJson<ClassroomData>(r.data),
  }));
}

/**
 * Parse stringified fields into JSON:
 * - `aggregations`
 */
export const parseSearchResults = (input?: SearchableResourceConnectionNew) => {
  const next: ParsedSearchableResourceConnectionNew = {
    ...input,
    aggregations:
      input?.aggregations && typeof input.aggregations === "string"
        ? JSON.parse(input.aggregations)
        : undefined,
  };

  return next;
};

export const parseItem = (input?: Resource) => {
  if (!input) return;
  const { content, contexts, ...data } = input;
  const next: ParsedItem = {
    ...data,
    content: {
      ...content,
      canvas: [],
    },
  };

  if (content?.canvas && next.content?.canvas) {
    next.content.canvas = filterMaybeArray(content.canvas).map((c) => {
      const config =
        c.config && typeof c.config === "string"
          ? JSON.parse(c.config)
          : c.config;
      const content =
        c.content && typeof c.content === "string"
          ? JSON.parse(c.content)
          : c.content;
      const scoring =
        c.config && typeof c.scoring === "string"
          ? JSON.parse(c.scoring)
          : c.scoring;

      if (c.type === "stimulus") {
        return {
          id: c.id,
          type: "stimulus" as const,
          content: {
            prompt: content?.prompt || { body: "" },
          },
        } as Extract<ElementNew, { type: "stimulus" }>;
      } else if (c.type === "stem") {
        return {
          ...c,
          type: "stem" as const,
          content,
          config,
          scoring,
        } as Extract<ElementNew, { type: "stem" }>;
      } else {
        return {
          id: c.id,
          type: "rubric" as const,
        } as Extract<ElementNew, { type: "rubric" }>;
      }
    });
  }

  if (input.contexts) {
    next.contexts = getContextsSafely(input.contexts);
  }

  return next;
};

export type ParsedStemExam = Replace<
  Resource,
  {
    questions: Maybe<
      Array<
        Replace<
          Resource,
          {
            content: { canvas: PrintableElement[] };
            contexts: Resource[];
            alignments: Objective[];
          }
        >
      >
    >;
  }
>;

type ResourceElementWithContent = Replace<ResourceElement, { content: string }>;

type PrintableElement = Extract<ElementNew, { type: "stimulus" | "stem" }>;

export const supportedItemType = (
  item: Pick<ResourceElement, "interactionType" | "type">,
) => {
  const supported = [
    InteractionTypeEnum.Choice,
    InteractionTypeEnum.TextInline,
    InteractionTypeEnum.TextGapMatch,
    InteractionTypeEnum.TextExtended,
    InteractionTypeEnum.TextSelectable,
    InteractionTypeEnum.MatchTable,
  ] as string[];

  // stimulus has no interaction type
  if (item.type === "stimulus") return true;

  return supported.includes(item.interactionType ?? "");
};

const printableContent = (
  item: ResourceElement,
): item is ResourceElementWithContent => !!item.content;

export const parseItemForPrint = <
  T extends { content?: Maybe<{ canvas?: Maybe<ResourceContent["canvas"]> }> },
>(
  draft: T,
): PrintableElement[] => {
  const parsed = filterMaybeArray(draft?.content?.canvas)
    .filter(printableContent)
    .map((el) => {
      const parsedStem: PrintableElement = Object.create(el);

      parsedStem.content = parseAwsJson<PrintableElement["content"]>(
        el.content,
      );

      if (parsedStem.type === "stem") {
        parsedStem.config = parseAwsJson<Stem.Config>(el.config);
        parsedStem.scoring = parseAwsJson<Stem.Scoring>(el.scoring);
      }
      return parsedStem;
    });

  // only return items where EVERY element is supported
  if (parsed.every(supportedItemType)) {
    return parsed;
  }

  return [];
};

type ParseExamForPrintOptions = {
  shuffleItems?: boolean;
  shuffleSeed?: string;
};
export const parseExamForPrint = (
  input: Resource,
  options: ParseExamForPrintOptions,
) => {
  const output: ParsedStemExam = Object.create(input);
  if (output?.questions) {
    output.questions = filterMaybeArray(input.questions).map((question) => {
      const { content, contexts, alignments, ...rest } = question;
      return {
        content: {
          canvas: parseItemForPrint(question),
        },
        contexts: filterMaybeArray(getContextsSafely(contexts)), // @TODO: Make a typesafe Passage type/parser
        alignments: getAlignmentsSafely(alignments),
        ...rest,
      };
    });

    // We can only shuffle if we have a seed
    if (options.shuffleItems && options.shuffleSeed) {
      output.questions = shuffleArray(options.shuffleSeed, output.questions);
    }
  }

  return output;
};

export function parseExam(input: Resource): ParsedExam;
export function parseExam(input: Resource | undefined): ParsedExam | undefined;
export function parseExam(
  input: Resource | null | undefined,
): ParsedExam | null | undefined;
export function parseExam(
  input: Resource | null | undefined,
): ParsedExam | null | undefined {
  return parseResource(input, (r) => ({
    questions: r.questions
      ?.map((question) => {
        parseItemCanvas(question);
        return question;
      })
      .filter(exists),
    data: parseAwsJson(r.data),
    config: parseAwsJson<ExamConfigData>(r.config) ?? {},
  }));
}

export function parseExamConfig(input: Resource): ParsedExamConfig;
export function parseExamConfig(
  input: Resource | undefined,
): ParsedExamConfig | undefined;
export function parseExamConfig(
  input: Resource | null | undefined,
): ParsedExamConfig | null | undefined;
export function parseExamConfig(
  input: Resource | null | undefined,
): ParsedExamConfig | null | undefined {
  return parseResource(input, (r) => ({
    config: parseAwsJson<ExamConfigData>(r.config) ?? {},
  }));
}

export const getAlignmentsFromExamItems = (exam: Resource) => {
  const alignments: Objective[] = [];
  if (exam?.questions) {
    exam.questions.forEach((question) => {
      if (question?.alignments) {
        getAlignmentsSafely(question.alignments).forEach((alignment) => {
          if (!alignment) return;
          if (alignments.find((a) => a.id === alignment.id)) return;
          alignments.push(alignment);
        });
      }
    });
  }

  return alignments;
};

export const parseObjectivesItem = (input: ObjectivesItem) => {
  const { data, ...rest } = input;
  const next: ParsedObjectivesItem = {
    ...rest,
    data: parseAwsJson<{ content?: string; notes?: string }>(data),
  };

  return next;
};

export function parseUnitObjective(input: ObjectivesItem): ParsedUnitObjective;
export function parseUnitObjective(
  input: ObjectivesItem | null | undefined,
): ParsedUnitObjective | null | undefined;
export function parseUnitObjective(
  input: ObjectivesItem | null | undefined,
): ParsedUnitObjective | null | undefined {
  if (input == null) return input;
  if (input.objective == null) {
    throw new Error(`objective property was not fetched for ${input.id}`);
  }

  const { objective, ...objectivesItem } = input;

  return {
    ...objective,
    id: objective.id ?? objectivesItem.id,
    objectivesItem: parseObjectivesItem(objectivesItem),
  };
}

export function parseObjectivesView(
  input: ObjectivesView,
): ParsedObjectivesView;
export function parseObjectivesView(
  input: ObjectivesView | undefined,
): ParsedObjectivesView | undefined;
export function parseObjectivesView(
  input: ObjectivesView | undefined,
): ParsedObjectivesView | undefined {
  if (input == null) return undefined;

  switch (input.type as ObjectivesViewType) {
    case "CLARIFICATION":
      return {
        ...input,
        type: "CLARIFICATION",
        data: parseAwsJson<ClarificationData>(input.data),
      };
    case "CONCEPTUAL_ALIGNMENT":
      return {
        ...input,
        type: "CONCEPTUAL_ALIGNMENT",
        data: parseAwsJson<ConceptualAlignmentData>(input.data),
      };
    case "VERTICAL_ALIGNMENT":
      return {
        ...input,
        type: "VERTICAL_ALIGNMENT",
        data: parseAwsJson<VerticalAlignmentData>(input.data),
      };
  }
}

export function parseWorkspaceUser(input: Workspace): ParsedWorkspaceUser;
export function parseWorkspaceUser(
  input: Workspace | undefined,
): ParsedWorkspaceUser | undefined;
export function parseWorkspaceUser(
  input: Workspace | undefined,
): ParsedWorkspaceUser | undefined {
  if (input === undefined) return undefined;
  if (!input.sk.startsWith("workspace-user#")) {
    throw new Error(`Record ${itemKey(input)} is not a workspace user.`);
  }

  const { workspaceUserId } = input;

  if (workspaceUserId == null) {
    throw new Error(
      `Record ${itemKey(input)} is missing workspaceUserId property.`,
    );
  }
  return parseResource(input, (r) => ({
    workspaceUserId,
    acl: parseAwsJson<WorkspaceAcl>(input.acl),
  }));
}

export function parseWorkspaceShare(
  input: WorkspaceShare,
): ParsedWorkspaceShare;
export function parseWorkspaceShare(
  input: WorkspaceShare | undefined,
): ParsedWorkspaceShare | undefined;
export function parseWorkspaceShare(
  input: WorkspaceShare | undefined | null,
): ParsedWorkspaceShare | undefined | null;
export function parseWorkspaceShare(
  input: WorkspaceShare | undefined | null,
): ParsedWorkspaceShare | undefined | null {
  return parseResource(input, (r) => ({
    definition: parseAwsJson<WorkspaceShareDefinition>(r.definition),
    allowFilter: parseAwsJson<SearchableResourceFilter>(r.allowFilter),
  }));
}

export function parseProduct(input: Product): ParsedProduct;
export function parseProduct(
  input: Product | undefined,
): ParsedProduct | undefined;
export function parseProduct(
  input: Product | undefined,
): ParsedProduct | undefined {
  return parseResource(input, (r) => ({
    definition: parseAwsJson<ProductDefinition>(r.definition),
  }));
}

export function parseResource<
  TResource extends object,
  TReplacements extends SameKeys<Partial<TResource>>,
>(
  obj: TResource,
  parse: (r: TResource) => TReplacements,
): Replace<TResource, TReplacements>;
export function parseResource<
  TResource extends object | undefined,
  TReplacements extends SameKeys<Partial<TResource>>,
>(
  obj: TResource | undefined,
  parse: (r: Required<TResource>) => TReplacements,
): Replace<TResource, TReplacements> | undefined;
export function parseResource<
  TResource extends object | null | undefined,
  TReplacements extends SameKeys<Partial<TResource>>,
>(
  obj: TResource | null | undefined,
  parse: (r: Required<TResource>) => TReplacements,
): Replace<TResource, TReplacements> | null | undefined;
export function parseResource<
  TResource extends object | null | undefined,
  TReplacements extends SameKeys<Partial<TResource>>,
>(
  obj: TResource | null | undefined,
  parse: (r: TResource) => TReplacements,
): Replace<TResource, TReplacements> | null | undefined {
  if (obj === undefined) return undefined;
  if (obj === null) return null;

  return {
    ...obj,
    ...parse(obj),
  };
}

// We have a few places that still assume ExamSessions are type Resource to clean up
// such as anything using elasticsearch :(
// This union input type can be removed once those are cleaned up
export const parseExamSession = (examSession: ExamSession | Resource) => {
  if (
    examSession.examSessionResponses &&
    typeof examSession.examSessionResponses === "string"
  ) {
    examSession.examSessionResponses = JSON.parse(
      examSession.examSessionResponses,
    );
  }
  if (
    examSession.examSessionResults &&
    typeof examSession.examSessionResults === "string"
  ) {
    const results = JSON.parse(examSession.examSessionResults);
    examSession.examSessionResults = results;
  }
  if (examSession.config && typeof examSession.config === "string") {
    examSession.config = JSON.parse(examSession.config);
  }

  return examSession as ParsedExamSession;
};

export const parseExamAttempt = (examAttempt: ExamAttempt) => {
  const examSessionResponses =
    parseAwsJson<AnyObject>(examAttempt.examSessionResponses) ?? {};
  const examSessionResults =
    parseAwsJson<ExamSessionResults>(examAttempt.examSessionResults) ?? null;
  const examBlobs = parseAwsJson<ExamBlobFiles>(examAttempt.examBlobs) ?? null;
  const highlights =
    parseAwsJson<ExamHighlights>(examAttempt.highlights) ?? null;

  return {
    ...examAttempt,
    examSessionResponses,
    examSessionResults,
    examBlobs,
    highlights,
  } satisfies ParsedExamAttempt;
};
